import { Injectable, inject, signal, DestroyRef } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { UIConfig, UIConfigFeatures, UIPageConfig } from '../../models/ui-config.model';
import { PageKey } from '../../models/page-key.enum';
import { TenantContextService } from '../../../core/tenant/tenant-context.service';
import { TENANT_UI_CONFIG_SOURCE } from './tenant-ui-config.registry';

/**
 * Stateful singleton that owns the current tenant's UI configuration.
 *
 * Listens to TenantContextService for tenant changes and automatically
 * loads the corresponding config. Consumers read the reactive `config`
 * signal (or `config$` observable) — no per-component loading boilerplate.
 */
@Injectable({ providedIn: 'root' })
export class UiConfigService {
  private tenantContext = inject(TenantContextService);
  private destroyRef = inject(DestroyRef);
  private uiConfigSource = inject(TENANT_UI_CONFIG_SOURCE);

  /** The loaded UIConfig for the current tenant (null while loading or on error). */
  readonly config = signal<UIConfig | null>(null);

  /** True while a config HTTP request is in-flight. */
  readonly loading = signal(false);

  /** Observable mirror of `config` for rxjs-based consumers (DrilldownPageService, etc.). */
  private readonly _config$ = new BehaviorSubject<UIConfig | null>(null);
  readonly config$ = this._config$.asObservable();
  private configSub: Subscription | null = null;
  private loadedConfigTenantKey = '';

  constructor() {
    this.subscribeToTenantChanges();
  }

  tenantKey(): string {
    return this.normalizeTenantKey(this.tenantContext.currentTenantKey());
  }

  private subscribeToTenantChanges(): void {
    const sub = this.tenantContext
      .tenantKeyChanges()
      .pipe(
        map((tenantKey) => this.normalizeTenantKey(tenantKey)),
        distinctUntilChanged()
      )
      .subscribe((tenantKey) => {
        this.configSub?.unsubscribe();

        // Reset when tenant changes to avoid projecting stale tenant config.
        this.setConfig(null);
        this.loading.set(true);

        this.configSub = this.loadTenantConfig(tenantKey).subscribe({
          next: (cfg) => {
            if (tenantKey !== this.tenantKey()) return;
            this.setConfig(cfg, tenantKey);
            this.loading.set(false);
          },
          error: () => {
            if (tenantKey !== this.tenantKey()) return;
            this.setConfig(null);
            this.loading.set(false);
          },
        });
      });

    this.destroyRef.onDestroy(() => {
      sub.unsubscribe();
      this.configSub?.unsubscribe();
    });
  }

  private normalizeTenantKey(tenantKey: string): string {
    return this.uiConfigSource.normalizeTenantKey(tenantKey);
  }

  private setConfig(cfg: UIConfig | null, tenantKey = ''): void {
    this.config.set(cfg);
    this.loadedConfigTenantKey = cfg ? this.normalizeTenantKey(tenantKey) : '';
    this._config$.next(cfg);
  }

  /**
   * Load tenant-specific UI configuration via the injected source.
   *
   * In production the source is HTTP (with localStorage cache + ETag); in
   * test/local it can be the bundled source. Errors propagate so the
   * caller can decide how to handle them (the subscriber in
   * ``subscribeToTenantChanges`` clears the signal on error).
   */
  loadTenantConfig(tenantKey: string): Observable<UIConfig> {
    const key = this.normalizeTenantKey(tenantKey);
    if (!key) {
      return throwError(() => new Error('Tenant key is required to load UI config'));
    }
    return this.uiConfigSource.resolve(key).pipe(
      switchMap((resolved) => {
        if (!resolved) {
          return throwError(() => new Error(`No UI config available for tenant "${key}"`));
        }
        return [resolved];
      })
    );
  }

  hasConfigForTenant(tenantKey: string = this.tenantKey()): boolean {
    const key = this.normalizeTenantKey(tenantKey);
    if (!key) return false;
    return this.loadedConfigTenantKey === key && this.config() !== null;
  }

  /** Return the page config object for a given page key. */
  getPageConfig(config: UIConfig | null | undefined, pageKey: string): UIPageConfig | null {
    if (!config) return null;
    return config.pages?.[pageKey] || null;
  }

  /** Return features bag for a page. */
  getPageFeatures(
    config: UIConfig | null | undefined,
    pageKey: PageKey | string
  ): UIConfigFeatures | null {
    if (!config) return null;
    const page = this.getPageConfig(config, pageKey);
    return page?.features || null;
  }

  /** Convenience accessor for metrics feature for a given page key. */
  getMetricsFeature(config: UIConfig | null | undefined, pageKey: PageKey | string) {
    return this.getPageFeatures(config, pageKey)?.metrics;
  }
}
